/**
 * Payout tier resolution — mirrors defaults in frontend PayoutRulesBuilder.jsx
 * so mobile behaviour matches what is saved per venue.
 *
 * Slot logic must stay aligned with `backend/src/utils/payoutSignerSlots.js` (Jest) and
 * `frontend/src/pages/payout-safe/payoutPolicyUtils.js`.
 * The TIERS list and PV1-PV4 defaults are drift-checked by
 * `backend/tests/modules/16-payout-policy-sync.guard.test.js`.
 *
 * Role identifiers are `staff_roles.role_key` values; `patron` is a reserved
 * sentinel for the player. Display labels come from the staff_roles lookup
 * delivered by the payout-config API, not from this file.
 */

const TIERS = ["PV1", "PV2", "PV3", "PV4"];

const defaultPolicyForTier = (tierId) => {
  const base = (enabled, kyc, occ, minA, maxA, cash, pin, sig, signers, issuer) => ({
    enabled,
    kycRequired: kyc,
    occupationRequired: occ,
    minAmount: minA,
    maxAmount: maxA,
    cashDispensed: cash,
    pinEntryByRoleId: pin,
    signaturesRequired: sig,
    signerRoleIds: signers,
    authorizationChain: [],
    issuerRoleId: issuer,
  });
  switch (tierId) {
    case "PV1":
      return base(true, false, false, 0, 2000, "CRT_OR_CASH", null, 0, ["patron", "cashier"], "cashier");
    case "PV2":
      return base(true, false, false, 2000, 5000, "CASH_ONLY", null, 0, ["patron", "cashier", "duty_manager"], "duty_manager");
    case "PV3":
      return base(true, true, false, 5000, 10000, "NO_CASH", "duty_manager", 0, ["patron", "duty_manager", "gaming_manager"], "gaming_manager");
    case "PV4":
    default:
      return base(true, true, false, 10000, null, "NO_CASH", "duty_manager", 2, ["patron", "duty_manager", "gaming_manager", "venue_manager"], "gaming_manager");
  }
};

export function ensureVenuePolicy(venue) {
  const policy = { ...(venue.payoutPolicy || {}) };
  TIERS.forEach((t) => {
    const def = defaultPolicyForTier(t);
    const existing = policy[t] || {};
    const merged = { ...def };
    for (const [k, v] of Object.entries(existing)) {
      if (v !== undefined) merged[k] = v;
    }
    if (Array.isArray(merged.authorizationChain) && merged.authorizationChain.length > 0) {
      if (!merged.signerRoleIds?.length) {
        merged.signerRoleIds = [...merged.authorizationChain];
      }
      merged.authorizationChain = [];
    }
    policy[t] = merged;
  });
  return { ...venue, payoutPolicy: policy };
}

/**
 * Pick highest matching slab (PV4 → PV1) so boundary amounts use the higher tier
 * (e.g. $10,000 → PV4 when PV3 max is $10,000 and PV4 min is $10,000).
 */
export function resolveTierForAmount(amount, payoutPolicy) {
  const ensured = ensureVenuePolicy({ payoutPolicy: payoutPolicy || {} }).payoutPolicy;
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return null;

  for (const tid of ["PV4", "PV3", "PV2", "PV1"]) {
    const p = ensured[tid];
    if (!p || !p.enabled) continue;
    const min = p.minAmount ?? 0;
    const max = p.maxAmount;
    if (num >= min && (max === null || max === undefined || max === "" || num <= max)) {
      return { tierId: tid, policy: p };
    }
  }
  return null;
}

const PATRON_ROLE_ID = "patron";

/** Patron sentinel role label (the player is not a staff_roles entry). */
export const PATRON_LABEL = "Player";

/** If patron is present, show Player signature first (stable UX). */
function sortPatronFirst(roleIds) {
  const idx = roleIds.indexOf(PATRON_ROLE_ID);
  if (idx <= 0) return roleIds;
  const next = [...roleIds];
  next.splice(idx, 1);
  next.unshift(PATRON_ROLE_ID);
  return next;
}

/**
 * Roles that must sign: signer roles (then issuer if not already included).
 */
function collectSignatureRoleIds(policy) {
  if (!policy || typeof policy !== "object") return [];
  const out = [];
  const seen = new Set();
  const push = (id) => {
    if (id == null || id === "" || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  for (const id of policy.signerRoleIds || []) push(id);
  if (policy.issuerRoleId) push(policy.issuerRoleId);
  return out;
}

/**
 * Signature slots for the payout step. `roleId` is a staff_roles.role_key (or
 * the `patron` sentinel); the caller resolves the display label from the
 * staff_roles lookup. `label` here is the role_key as a safe fallback.
 */
export function getSignerSlots(policy) {
  if (!policy) return [];
  const fromConfiguredRoles = collectSignatureRoleIds(policy);
  if (fromConfiguredRoles.length > 0) {
    return sortPatronFirst(fromConfiguredRoles)
      .slice(0, 8)
      .map((roleId, i) => ({
        key: `slot-${i}-${roleId}`,
        roleId,
        label: roleId,
      }));
  }
  const n = Math.min(Math.max(0, policy.signaturesRequired ?? 0), 5);
  if (n === 0) return [];
  const roleIdsFromPolicy = Array.isArray(policy.signerRoleIds) ? policy.signerRoleIds.filter(Boolean) : [];
  const defaults = ["cashier", "duty_manager", "gaming_manager", "venue_manager"];
  const slots = [];
  for (let i = 0; i < n; i++) {
    const roleId = roleIdsFromPolicy[i] ?? defaults[i] ?? "cashier";
    slots.push({
      key: `slot-${i}-${roleId}`,
      roleId,
      label: roleId,
    });
  }
  return slots;
}
